import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthContext";
import { API_BASE } from "../config/api";
import CountryPhoneInput from "./CountryPhoneInput";

// Fallback if API_BASE isn't exported in your config
const BASE = typeof API_BASE === "string" ? API_BASE : "";

function toArray(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  if (data && Array.isArray(data.items)) return data.items;
  if (data && typeof data === "object") return Object.values(data);
  return [];
}

function normalizeLocalPhone(input) {
  if (!input) return "";
  // Very permissive: remove spaces, dashes; keep + and digits
  const s = String(input).trim().replace(/[ \-()]/g, "");
  return s;
}

function formatPaymentAmount(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "";
  return (Math.round(number * 100) / 100).toFixed(2);
}

function splitCashCardAmounts(field, rawValue, billTotal) {
  const otherField = field === "cash" ? "card" : "cash";
  if (rawValue === "") {
    return { [field]: "", [otherField]: "" };
  }

  const entered = Number(rawValue || 0);
  const remaining = Math.max(Number(billTotal || 0) - (Number.isFinite(entered) ? entered : 0), 0);
  return {
    [field]: rawValue,
    [otherField]: formatPaymentAmount(remaining),
  };
}

function useLocalDrafts(key) {
  const load = () => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };
  const [drafts, setDrafts] = useState(load);

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(drafts));
    } catch {}
  }, [key, drafts]);

  return [drafts, setDrafts];
}

// Local calendar date as YYYY-MM-DD. Deliberately not toISOString(), which
// converts to UTC and can report yesterday for a late-evening bill.
function todayISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const EMPTY_DRAFT = () => ({
  id: `draft-${Date.now()}`,
  billDate: "", // empty = today; a past date marks this as a filled-in day
  customer: null, // {id, name, phone, note?}
  lines: [], // {kind:'service'|'item', id, name, unitPrice, qty, staffId, serviceId/itemId, flexible}
  paymentMethod: "pending",
  paymentMode: "pending",
  paymentAmounts: { cash: "", card: "", online: "" },
});

export default function BillingForm() {
  const { access, refreshAccessToken, logout } = useAuth() || {};

  // --- customer & search state
  const [searchText, setSearchText] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
	const [customerFormLanguage, setCustomerFormLanguage] = useState("EN");

  // --- global reference data
	const [servicesByCatalog, setServicesByCatalog] = useState({});
	const [staff, setStaff] = useState([]);
	const [staffForService, setStaffForService] = useState([]);
	const [items, setItems] = useState([]);


  // --- drafts
  const [drafts, setDrafts] = useLocalDrafts("billingDrafts_v2");
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [busy, setBusy] = useState(false);

  const activeDraft = useMemo(
    () => (activeDraftId ? drafts[activeDraftId] || null : null),
    [drafts, activeDraftId]
  );
  const currentCustomer = activeDraft?.customer || null;

  // --- note UI: bound to currentCustomer, saved separately
  const [noteText, setNoteText] = useState("");

  useEffect(() => {
    if (currentCustomer) {
      setNoteText(currentCustomer.note || "");
    } else {
      setNoteText("");
    }
  }, [currentCustomer?.id, activeDraftId]); // refresh note when switching drafts/customers

  // --- customer create/edit form
  const [customerFormOpen, setCustomerFormOpen] = useState(false);
  const [customerFormMode, setCustomerFormMode] = useState("new"); // 'new' | 'edit'
  const [customerFormName, setCustomerFormName] = useState("");
  const [customerFormPhone, setCustomerFormPhone] = useState("");

  // ---- helpers: auth fetch
  async function authFetch(url, options = {}) {
    const token = access || (await refreshAccessToken());
    if (!token) throw new Error("No auth token");

    const opts = { ...options };
    const headers = {
      ...(opts.headers || {}),
      Authorization: `Bearer ${token}`,
    };

    if (opts.body && typeof opts.body === "object" && !(opts.body instanceof FormData)) {
      headers["Content-Type"] = headers["Content-Type"] || "application/json";
      if (headers["Content-Type"] === "application/json") {
        opts.body = JSON.stringify(opts.body);
      }
    }

    return fetch(`${BASE}${url}`, { ...opts, headers });
  }

  // fetch with auth if possible, else anonymous
  async function fetchMaybeAuth(url) {
    try {
      const token = access || (await refreshAccessToken());
      if (token) {
        return await authFetch(url);
      }
    } catch {
      // ignore, fallback
    }
    return fetch(`${BASE}${url}`);
  }

		function handleAuthError() {
			// If AuthContext provides a logout, use it
			if (typeof logout === "function") {
				logout();
				return;
			}

			// Fallback: clear tokens and hard-redirect to login
			[
				"access",
				"refresh",
				"token",
				"tokenData",
				"user",
				"auth",
			].forEach((k) => localStorage.removeItem(k));

			window.location.href = "/login";
		}
  
  // ---- load reference data once
	useEffect(() => {
		(async () => {
			try {
				const sRes = await fetchMaybeAuth(`/api/services/`);
				if (sRes.status === 401 || sRes.status === 403) {
					handleAuthError();
					return;
				}
				const sJson = await sRes.json().catch(() => ({}));
				setServicesByCatalog(sJson && typeof sJson === "object" ? sJson : {});

				const stRes = await authFetch(`/api/staff/`);
				if (stRes.status === 401 || stRes.status === 403) {
					handleAuthError();
					return;
				}
				const stJson = await stRes.json().catch(() => []);
				setStaff(
					toArray(stJson)
						.map((u) => ({
							id: u.id ?? u.pk ?? u.user_id ?? u.staff_id ?? u,
							name: u.display_name ?? u.username ?? u.name ?? u.full_name ?? `Staff ${u.id ?? ""}`,
						}))
						.filter((x) => x.id)
				);

				const itRes = await authFetch(`/api/items/`);
				if (itRes.status === 401 || itRes.status === 403) {
					handleAuthError();
					return;
				}
				const itJson = await itRes.json().catch(() => []);
				setItems(
					toArray(itJson)
						.map((it) => ({
							id: it.id,
							name: it.name ?? it.item ?? `Item ${it.id}`,
							sell_price: Number(it.sell_price ?? it.price ?? 0),
              quantity: Number(it.quantity ?? 0),
              category: it.category || "",
						}))
						.filter((x) => x.id)
				);
			} catch (e) {
				console.error("Failed to load reference data", e);
			}
		})();
		// you can keep the eslint-disable line as you have now
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);



  // ---- draft management
  function ensureDraft() {
    if (activeDraft) return activeDraft;
    const draft = EMPTY_DRAFT();
    setDrafts((d) => ({ ...d, [draft.id]: draft }));
    setActiveDraftId(draft.id);
    return draft;
  }

  function updateDraft(mutator) {
    const d = ensureDraft();
    const updated = { ...d };
    mutator(updated);
    setDrafts((prev) => ({ ...prev, [updated.id]: updated }));
  }

  function newDraft() {
    const draft = EMPTY_DRAFT();
    setDrafts((d) => ({ ...d, [draft.id]: draft }));
    setActiveDraftId(draft.id);
  }

  function deleteDraft(id) {
    setDrafts((d) => {
      const copy = { ...d };
      delete copy[id];
      return copy;
    });
    if (activeDraftId === id) setActiveDraftId(null);
  }

  // ---- customer search
  async function onSearch() {
		console.log("onSearch clicked with:", searchText);
    const q = normalizeLocalPhone(searchText);
    if (!q || q.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const token = access || (await refreshAccessToken());
      if (!token) return;
      const res = await fetch(`${BASE}/api/search-customer/?query=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => []);
      setSearchResults(toArray(json));
    } finally {
      setSearching(false);
    }
  }

  // ---- quick create / edit customer form openers
  function openQuickCreate() {
    const phoneGuess = normalizeLocalPhone(searchText);
    setCustomerFormMode("new");
    setCustomerFormName("");
    setCustomerFormPhone(phoneGuess || "");
		setCustomerFormLanguage("EN");
    setCustomerFormOpen(true);
  }

  function openEditCustomer() {
    if (!currentCustomer) return;
    setCustomerFormMode("edit");
    setCustomerFormName(currentCustomer.name || "");
    setCustomerFormPhone(currentCustomer.phone || "");
		setCustomerFormLanguage(currentCustomer.language || "EN");
    setCustomerFormOpen(true);
  }

  // ---- save customer form (new or edit)
  async function saveCustomerForm() {
    const name = customerFormName.trim();
    const phoneRaw = customerFormPhone.trim();
    const phone = normalizeLocalPhone(phoneRaw);

    if (!phone) {
      alert("Phone number is required.");
      return;
    }
    if (!name) {
      alert("Name is required.");
      return;
    }

    setBusy(true);
    try {
      const token = access || (await refreshAccessToken());
      if (!token) return;

      if (customerFormMode === "new") {
        const res = await fetch(`${BASE}/api/create-customer/`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ phone, name, language: customerFormLanguage }),
        });
        const js = await res.json().catch(() => ({}));
        if (!res.ok || !js.customer_id) {
          alert(js.error || "Failed to create customer");
          return;
        }
        const savedName = js.name || name;
        const savedPhone = js.phone || phone;
        const savedLanguage = js.language || customerFormLanguage;
        const newCust = {
          id: js.customer_id,
          name: savedName,
          phone: savedPhone,
          language: savedLanguage,
          note: "",
        };
        updateDraft((d) => {
          d.customer = newCust;
        });
        setSearchResults([]);
      } else if (customerFormMode === "edit" && currentCustomer) {
        const res = await authFetch(`/api/update-customer/${currentCustomer.id}/`, {
          method: "PATCH",
          body: { name, phone, language: customerFormLanguage },
        });
        const js = await res.json().catch(() => ({}));
        if (!res.ok) {
          alert(js.error || "Failed to update customer");
          return;
        }
        const savedCustomer = js.customer || {};
        const savedName = savedCustomer.name || name;
        const savedPhone = savedCustomer.phone || phone;
        const savedLanguage = savedCustomer.language || customerFormLanguage;
        // Update this customer in all drafts
        setDrafts((prev) => {
          const copy = { ...prev };
          Object.keys(copy).forEach((key) => {
            const d = copy[key];
            if (d.customer && d.customer.id === currentCustomer.id) {
              copy[key] = {
                ...d,
                customer: {
                  ...d.customer,
                  name: savedName,
                  phone: savedPhone,
                  language: savedLanguage,
                },
              };
            }
          });
          return copy;
        });
      }

      setCustomerFormOpen(false);
    } finally {
      setBusy(false);
    }
  }

  // ---- save note (customer profile)
  async function saveNote() {
    if (!currentCustomer) {
      alert("Select a customer first.");
      return;
    }
    const note = noteText || "";
    setBusy(true);
    try {
      const res = await authFetch(`/api/update-customer/${currentCustomer.id}/`, {
        method: "PATCH",
        body: { note },
      });
      const js = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(js.error || "Failed to save note");
        return;
      }

      // Update note across all drafts that reference this customer
      setDrafts((prev) => {
        const copy = { ...prev };
        Object.keys(copy).forEach((key) => {
          const d = copy[key];
          if (d.customer && d.customer.id === currentCustomer.id) {
            copy[key] = {
              ...d,
              customer: {
                ...d.customer,
                note,
              },
            };
          }
        });
        return copy;
      });

      alert("Note saved ✅");
    } finally {
      setBusy(false);
    }
  }

  async function sendGoogleReview() {
    if (!currentCustomer) {
      alert("Select a customer first.");
      return;
    }
    setBusy(true);
    try {
      const res = await authFetch(`/api/customers/${currentCustomer.id}/send-google-review/`, {
        method: "POST",
      });
      const js = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(js.error || "Failed to send Google review message");
        return;
      }
      alert("Google review message sent.");
    } finally {
      setBusy(false);
    }
  }

  // ---- service / item adders
  const catalogNames = useMemo(() => Object.keys(servicesByCatalog || {}), [servicesByCatalog]);
  const [selCatalog, setSelCatalog] = useState("");
  const [selServiceId, setSelServiceId] = useState("");
  const [selServicePrice, setSelServicePrice] = useState("");
  const [selServiceFlexible, setSelServiceFlexible] = useState(false);
  const [selServiceStaffId, setSelServiceStaffId] = useState("");

  const [selItemId, setSelItemId] = useState("");
  const [selItemPrice, setSelItemPrice] = useState("");
  const [selItemStaffId, setSelItemStaffId] = useState("");
  const [selItemQty, setSelItemQty] = useState(1);

  const servicesInCatalog = useMemo(() => {
    const list = (servicesByCatalog && servicesByCatalog[selCatalog]) || [];
    return Array.isArray(list) ? list : [];
  }, [servicesByCatalog, selCatalog]);


  useEffect(() => {
    if (!selServiceId) {
      setStaffForService([]); // no service -> no filtering
      return;
    }

    (async () => {
      try {
        const res = await authFetch(`/api/staff/by-service/${selServiceId}/`);
        if (!res.ok) {
          setStaffForService([]);
          return;
        }
        const js = await res.json().catch(() => []);
        setStaffForService(toArray(js));
      } catch (e) {
        console.error("Failed to load staff for service", e);
        setStaffForService([]);
      }
    })();
  }, [selServiceId]);


  function onPickService(idStr) {
    setSelServiceId(idStr);
    const svc = servicesInCatalog.find((s) => String(s.id) === String(idStr));
    const price = svc ? Number(svc.price || 0) : 0;
    setSelServicePrice(price ? String(price) : "");
    setSelServiceFlexible(Boolean(svc?.is_flexible_price));
  }

  function addServiceLine() {
    if (!activeDraft) ensureDraft();
    const svc = servicesInCatalog.find((s) => String(s.id) === String(selServiceId));
    if (!svc) {
      alert("Pick a service");
      return;
    }
    if (!selServiceStaffId) {
      alert("Pick a staff");
      return;
    }
    const unitPrice = Number(selServicePrice || svc.price || 0) || 0;

    updateDraft((d) => {
      d.lines.push({
        kind: "service",
        id: `svc-${Date.now()}`,
        name: svc.name,
        unitPrice,
        qty: 1,
        staffId: Number(selServiceStaffId),
        serviceId: Number(svc.id),
        flexible: Boolean(svc.is_flexible_price),
      });
    });

    setSelServiceId("");
    setSelServicePrice("");
    setSelServiceStaffId("");
    setSelServiceFlexible(false);
  }

  function addItemLine() {
    if (!activeDraft) ensureDraft();
    const it = items.find((x) => String(x.id) === String(selItemId));
    if (!it) {
      alert("Pick an item");
      return;
    }
    if (!selItemStaffId) {
      alert("Pick a staff");
      return;
    }
    const qty = Math.max(1, Number(selItemQty || 1));
    const unitPrice = Number(selItemPrice || it.sell_price || 0) || 0;
    if (it.quantity < qty) {
      alert(`Only ${it.quantity} available for ${it.name}.`);
      return;
    }

    updateDraft((d) => {
      d.lines.push({
        kind: "item",
        id: `itm-${Date.now()}`,
        name: it.name,
        unitPrice,
        qty,
        staffId: Number(selItemStaffId),
        itemId: Number(it.id),
      });
    });

    setSelItemId("");
    setSelItemPrice("");
    setSelItemQty(1);
    setSelItemStaffId("");
  }

  function removeLine(lineId) {
    updateDraft((d) => {
      d.lines = d.lines.filter((l) => l.id !== lineId);
    });
  }

  const total = useMemo(() => {
    if (!activeDraft) return 0;
    return activeDraft.lines.reduce(
      (sum, l) => sum + Number(l.unitPrice || 0) * Number(l.qty || 1),
      0
    );
  }, [activeDraft]);

  // ---- submit bill
  async function submitBill() {
    if (!activeDraft) {
      alert("No draft");
      return;
    }
    if (!currentCustomer) {
      alert("Select a customer first");
      return;
    }
    if (!activeDraft.lines.length) {
      alert("Add at least one service or item");
      return;
    }
    const mode = activeDraft?.paymentMode || activeDraft?.paymentMethod || "pending";
    if (mode === "cash_card") {
      const amounts = activeDraft?.paymentAmounts || {};
      const paid = Number(amounts.cash || 0) + Number(amounts.card || 0);
      if (Math.abs(paid - total) > 0.01) {
        alert(`Cash + Card total must equal AED ${total.toFixed(2)}.`);
        return;
      }
    }

    const serviceLines = activeDraft.lines.filter((l) => l.kind === "service");
    const itemsPayload = activeDraft.lines.filter((l) => l.kind === "item");

    const service_ids = serviceLines.map((l) => ({
      id: l.serviceId,
      staff_id: l.staffId,
    }));

    const discounts = {};
    for (const l of serviceLines) {
      if (l.unitPrice != null && l.unitPrice !== "") {
        discounts[String(l.serviceId)] = Number(l.unitPrice);
      }
    }

    setBusy(true);
    try {
      let billId = null;

      const billDate = activeDraft.billDate || todayISO();
      const isBackdated = billDate < todayISO();

      if (isBackdated) {
        const ok = window.confirm(
          `This bill will be recorded for ${billDate}, not today.

No WhatsApp message will be sent, and stock will not change.

Continue?`
        );
        if (!ok) return;
      }

      const payload = {
        customer_id: currentCustomer.id,
        // Only sent when filling in a past day. A bill for today must keep its
        // real time of sale rather than being anchored to a fixed hour.
        ...(isBackdated ? { bill_date: billDate } : {}),
        payment_method: activeDraft.paymentMode || activeDraft.paymentMethod || "pending",
        payments: buildPaymentsPayload(),
        service_ids,
        discounts,
        items: itemsPayload.map((it) => ({
          item_id: it.itemId,
          staff_id: it.staffId,
          quantity: it.qty,
          unit_price: it.unitPrice,
        })),
      };

      const res = await authFetch(`/api/create-bill/`, {
        method: "POST",
        body: payload,
      });
      const js = await res.json().catch(() => ({}));

      if (!res.ok || !js.bill_id) {
        alert(js.error || "Failed to create bill");
        return;
      }
      billId = js.bill_id;
      alert(
        js.backdated
          ? `Bill #${billId} recorded for ${billDate}. No WhatsApp sent, stock unchanged.`
          : `Bill #${billId} created.`
      );
      deleteDraft(activeDraft.id);
      await refreshItems();
      return;

      // eslint-disable-next-line no-unreachable
      const itemErrors = [];

      for (const it of itemsPayload) {
        try {
          const itemRes = await authFetch(`/api/items/transaction/`, {
            method: "POST",
            body: {
              item_id: it.itemId,
              staff_id: it.staffId,
              customer_id: currentCustomer.id,
              quantity: it.qty,
              unit_price: it.unitPrice,
              bill_id: billId,
              // transaction_type defaults to "sale"
            },
          });
          if (!itemRes.ok) {
            itemErrors.push(`Item "${it.name}" failed to save.`);
          }
        } catch {
          itemErrors.push(`Item "${it.name}" failed to save.`);
        }
      }

      if (itemErrors.length) {
        alert(
          `Bill #${billId} created, but some items failed:\n` +
            itemErrors.join("\n")
        );
      } else {
        alert(`Bill #${billId} created ✅`);
      }

      deleteDraft(activeDraft.id);
    } finally {
      setBusy(false);
    }
  }

  function buildPaymentsPayload() {
    const mode = activeDraft?.paymentMode || activeDraft?.paymentMethod || "pending";
    const amounts = activeDraft?.paymentAmounts || {};
    if (mode === "pending") return [{ payment_method: "pending", amount: 0 }];
    if (mode === "cash_card") {
      return [
        { payment_method: "cash", amount: Number(amounts.cash || 0) },
        { payment_method: "card", amount: Number(amounts.card || 0) },
      ].filter((payment) => payment.amount > 0);
    }
    return [{ payment_method: mode, amount: total }];
  }

  async function refreshItems() {
    try {
      const itRes = await authFetch(`/api/items/`);
      if (!itRes.ok) return;
      const itJson = await itRes.json().catch(() => []);
      setItems(
        toArray(itJson)
          .map((it) => ({
            id: it.id,
            name: it.name ?? it.item ?? `Item ${it.id}`,
            sell_price: Number(it.sell_price ?? it.price ?? 0),
            quantity: Number(it.quantity ?? 0),
            category: it.category || "",
          }))
          .filter((x) => x.id)
      );
    } catch (err) {
      console.error("Failed to refresh items", err);
    }
  }

  // ---- UI
  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "1rem" }}>
      {/* Draft switcher */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <div>
          <strong>Drafts:</strong>{" "}
          {Object.keys(drafts).length === 0 && <em>No drafts</em>}
          {Object.values(drafts).map((d) => (
            <button
              key={d.id}
              onClick={() => setActiveDraftId(d.id)}
              style={{
                marginRight: 6,
                padding: "4px 8px",
                border:
                  activeDraftId === d.id
                    ? "2px solid #ff69b4"
                    : "1px solid #ccc",
                borderRadius: 6,
                cursor: "pointer",
                background: activeDraftId === d.id ? "#ffe6f3" : "white",
              }}
            >
              {d.customer
                ? `${d.customer.name || "(no name)"} (${d.customer.phone})`
                : "New bill"}
            </button>
          ))}
        </div>
        <div>
          <button onClick={newDraft} style={{ marginRight: 8 }}>
            + New Draft
          </button>
          {activeDraft && (
            <button onClick={() => deleteDraft(activeDraft.id)}>
              Delete Draft
            </button>
          )}
        </div>
      </div>

      {/* Customer block */}
      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 10,
          padding: 12,
          marginBottom: 12,
        }}
      >
        <h3 style={{ marginTop: 0 }}>Customer</h3>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by name or phone"
            style={{ flex: "1 1 360px" }}
          />
          <button onClick={onSearch} disabled={searching}>
            {searching ? "Searching..." : "Search"}
          </button>
          <button onClick={openQuickCreate} disabled={busy}>
            Quick Create
          </button>
          <button
            onClick={() => {
              if (!activeDraft) return;
              updateDraft((d) => {
                d.customer = null;
              });
              setNoteText("");
              setSearchResults([]);
            }}
          >
            Clear
          </button>
        </div>

        {/* Quick create / edit form */}
				{customerFormOpen && (
					<div
						style={{
							marginTop: 10,
							padding: 10,
							borderRadius: 8,
							border: "1px solid #ddd",
							background: "#fafafa",
						}}
					>
						<div style={{ marginBottom: 6 }}>
							<strong>
								{customerFormMode === "new"
									? "Create New Customer"
									: "Edit Customer"}
							</strong>
						</div>

						<div
							style={{
								display: "flex",
								flexWrap: "wrap",
								gap: 8,
								alignItems: "center",
							}}
						>
							{/* Name */}
							<input
								type="text"
								value={customerFormName}
								onChange={(e) => setCustomerFormName(e.target.value)}
								placeholder="Name"
								style={{ flex: "1 1 200px" }}
							/>

							{/* Phone */}
							<CountryPhoneInput
								value={customerFormPhone}
								onChange={setCustomerFormPhone}
							/>

							{/* Language Dropdown */}
							<select
								value={customerFormLanguage}
								onChange={(e) => setCustomerFormLanguage(e.target.value)}
								style={{ flex: "1 1 200px" }}
							>
								<option value="EN">English</option>
								<option value="AR">Arabic</option>
								<option value="FR">French</option>
							</select>

							{/* Save Button */}
							<button onClick={saveCustomerForm} disabled={busy}>
								Save
							</button>

							{/* Cancel Button */}
							<button
								type="button"
								onClick={() => setCustomerFormOpen(false)}
								disabled={busy}
							>
								Cancel
							</button>
						</div>
					</div>
				)}
        {searchResults.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <select
              onChange={(e) => {
                const id = Number(e.target.value);
                const c = searchResults.find((x) => Number(x.id) === id);
                if (!c) return;
                const picked = {
                  id: c.id,
                  name: c.name, // as is (manager can later edit)
                  phone: c.phone,
                  note: c.note || "",
									language: c.language || null,
                };
                updateDraft((d) => {
                  d.customer = picked;
                });
                setSearchResults([]);
              }}
              defaultValue=""
            >
              <option value="" disabled>
                Pick a customer…
              </option>
              {searchResults.map((c) => (
                <option key={c.id} value={c.id}>
                  {(c.name || "").trim() || "(no name)"} — {c.phone}
                </option>
              ))}
            </select>
          </div>
        )}

        {currentCustomer && (
          <div
            style={{
              marginTop: 8,
              padding: 8,
              background: "#fff2fa",
              borderRadius: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div>
                <strong>Selected:</strong>{" "}
                {currentCustomer.name || "(no name)"} — {currentCustomer.phone}
              </div>
              <button onClick={openEditCustomer} disabled={busy}>
                Edit Customer
              </button>
            </div>

            <div style={{ marginTop: 8 }}>
              <label>
                <strong>Note (customer profile)</strong>
              </label>
              <textarea
                rows={3}
                style={{ width: "100%" }}
                placeholder="Notes about the customer (saved in profile)"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
              <div style={{ marginTop: 6 }}>
                <button onClick={saveNote} disabled={busy || !currentCustomer}>
                  Save Note
                </button>
                <button
                  onClick={sendGoogleReview}
                  disabled={busy || !currentCustomer}
                  style={{ marginLeft: 8 }}
                >
                  Send Google Review
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Service line */}
      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 10,
          padding: 12,
          marginBottom: 12,
        }}
      >
        <h3 style={{ marginTop: 0 }}>Add Service</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr auto",
            gap: 8,
            alignItems: "center",
          }}
        >
          <select
            value={selCatalog}
            onChange={(e) => {
              setSelCatalog(e.target.value);
              setSelServiceId("");
              setSelServicePrice("");
              setSelServiceFlexible(false);
            }}
          >
            <option value="">Select Category</option>
            {catalogNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <select
            value={selServiceId}
            onChange={(e) => onPickService(e.target.value)}
            disabled={!selCatalog}
          >
            <option value="">Select Service</option>
            {servicesInCatalog.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} — AED {Number(s.price ?? 0).toFixed(2)}
              </option>
            ))}
          </select>

					<select
						value={selServiceStaffId}
						onChange={(e) => setSelServiceStaffId(e.target.value)}
					>
						<option value="">Choose Staff</option>
						{(staffForService.length ? staffForService : staff).map((s) => (
							<option key={s.id} value={s.id}>
								{s.name}
							</option>
						))}
					</select>


          <input
            type="number"
            min="0"
            step="0.01"
            value={selServicePrice}
            onChange={(e) => setSelServicePrice(e.target.value)}
            disabled={!selServiceFlexible}
            placeholder={selServiceFlexible ? "Flexible Price" : "Price (fixed)"}
            title={
              selServiceFlexible
                ? "This service allows a flexible price"
                : "This service has a fixed price"
            }
          />

          <button onClick={addServiceLine}>Add</button>
        </div>
        {!selServiceFlexible && selServiceId && (
          <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
            Price is fixed by service configuration.
          </div>
        )}
      </div>

      {/* Add Item line */}
      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 10,
          padding: 12,
          marginBottom: 12,
        }}
      >
        <h3 style={{ marginTop: 0 }}>Add Retail Item</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr auto",
            gap: 8,
            alignItems: "center",
          }}
        >
          <select
            value={selItemId}
            onChange={(e) => {
              const id = e.target.value;
              setSelItemId(id);
              const it = items.find((x) => String(x.id) === String(id));
              setSelItemPrice(it ? String(it.sell_price || 0) : "");
            }}
          >
            <option value="">Select Item</option>
            {items.map((it) => (
              <option key={it.id} value={it.id}>
                {it.name} - AED {Number(it.sell_price ?? 0).toFixed(2)} - Qty {it.quantity}
              </option>
            ))}
          </select>

          <select
            value={selItemStaffId}
            onChange={(e) => setSelItemStaffId(e.target.value)}
          >
            <option value="">Choose Staff</option>
            {staff.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          <input
            type="number"
            min={1}
            value={selItemQty}
            onChange={(e) => setSelItemQty(e.target.value)}
            placeholder="Qty"
          />
          <input
            type="number"
            min="0"
            step="0.01"
            value={selItemPrice}
            onChange={(e) => setSelItemPrice(e.target.value)}
            placeholder="Unit Price"
          />

          <button onClick={addItemLine}>Add</button>
        </div>
      </div>

      {/* Lines table */}
      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 10,
          padding: 12,
          marginBottom: 12,
        }}
      >
        <h3 style={{ marginTop: 0 }}>Lines</h3>
        {!activeDraft || activeDraft.lines.length === 0 ? (
          <div style={{ color: "#666" }}>
            No lines yet. Add a service or an item.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th
                  style={{ textAlign: "left", borderBottom: "1px solid #eee" }}
                >
                  Type
                </th>
                <th
                  style={{ textAlign: "left", borderBottom: "1px solid #eee" }}
                >
                  Name
                </th>
                <th
                  style={{ textAlign: "right", borderBottom: "1px solid #eee" }}
                >
                  Qty
                </th>
                <th
                  style={{ textAlign: "right", borderBottom: "1px solid #eee" }}
                >
                  Unit
                </th>
                <th
                  style={{ textAlign: "right", borderBottom: "1px solid #eee" }}
                >
                  Total
                </th>
                <th style={{ borderBottom: "1px solid #eee" }}></th>
              </tr>
            </thead>
            <tbody>
              {activeDraft.lines.map((l) => (
                <tr key={l.id}>
                  <td>{l.kind}</td>
                  <td>{l.name}</td>
                  <td style={{ textAlign: "right" }}>{l.qty}</td>
                  <td style={{ textAlign: "right" }}>
                    AED {Number(l.unitPrice || 0).toFixed(2)}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    AED{" "}
                    {(
                      Number(l.unitPrice || 0) * Number(l.qty || 1)
                    ).toFixed(2)}
                  </td>
                  <td>
                    <button onClick={() => removeLine(l.id)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td
                  colSpan={4}
                  style={{ textAlign: "right", paddingTop: 8 }}
                >
                  <strong>Total</strong>
                </td>
                <td style={{ textAlign: "right", paddingTop: 8 }}>
                  <strong>AED {total.toFixed(2)}</strong>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Payment + actions */}
      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 10,
          padding: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
            marginBottom: 10,
          }}
        >
          <label htmlFor="bill-date">
            <strong>Bill date</strong>
          </label>
          <input
            id="bill-date"
            type="date"
            max={todayISO()}
            value={activeDraft?.billDate || todayISO()}
            onChange={(e) =>
              updateDraft((d) => {
                const v = e.target.value;
                d.billDate = v && v < todayISO() ? v : "";
              })
            }
          />
          {activeDraft?.billDate && activeDraft.billDate < todayISO() && (
            <span
              style={{
                background: "#fff4e5",
                border: "1px solid #ffb74d",
                color: "#8a4b00",
                borderRadius: 8,
                padding: "4px 10px",
                fontSize: 13,
              }}
            >
              Filling in {activeDraft.billDate} — no WhatsApp will be sent, stock will not change.
            </span>
          )}
          {activeDraft?.billDate && (
            <button type="button" onClick={() => updateDraft((d) => { d.billDate = ""; })}>
              Reset to today
            </button>
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <label>
            <strong>Payment method</strong>
          </label>
          <select
            value={activeDraft?.paymentMode || activeDraft?.paymentMethod || "pending"}
            onChange={(e) =>
              updateDraft((d) => {
                d.paymentMode = e.target.value;
                d.paymentMethod = e.target.value;
                d.paymentAmounts = d.paymentAmounts || { cash: "", card: "", online: "" };
              })
            }
          >
            <option value="pending">Prepare (pay later)</option>
            <option value="cash">Cash</option>
            <option value="card">Card</option>
            <option value="cash_card">Cash + Card</option>
            <option value="online">Online</option>
          </select>

          {(activeDraft?.paymentMode || activeDraft?.paymentMethod) === "cash_card" && (
            <>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Cash amount"
                value={activeDraft?.paymentAmounts?.cash || ""}
                onChange={(e) =>
                  updateDraft((d) => {
                    d.paymentAmounts = {
                      ...(d.paymentAmounts || {}),
                      ...splitCashCardAmounts("cash", e.target.value, total),
                    };
                  })
                }
                style={{ maxWidth: 150 }}
              />
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Card amount"
                value={activeDraft?.paymentAmounts?.card || ""}
                onChange={(e) =>
                  updateDraft((d) => {
                    d.paymentAmounts = {
                      ...(d.paymentAmounts || {}),
                      ...splitCashCardAmounts("card", e.target.value, total),
                    };
                  })
                }
                style={{ maxWidth: 150 }}
              />
            </>
          )}

          <button
            onClick={() => alert("Draft saved locally ✅")}
            disabled={!activeDraft}
          >
            Save Draft
          </button>
          <button onClick={submitBill} disabled={busy || !activeDraft}>
            Submit Bill
          </button>
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: "#666" }}>
          You can keep multiple drafts and switch between them above. Drafts are
          stored in this browser only.
        </div>
      </div>
    </div>
  );
}
